import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import ActivationChecklist from './components/ActivationChecklist/ActivationChecklist';
import { ReactComponent as RightArrowIcon } from '../../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as ChevronIcon } from '../../../../../../assets/icons/chevron-down.svg';
import { ReactComponent as PlusIcon } from '../../../../../../assets/icons/plus.svg';
import { ReactComponent as SuccessFill } from '../../../../../../assets/icons/success_fill.svg';
import CustomButton from '../../../../../../shared/components/Buttons/CustomButton/CustomButton';
import { PROSPECTS_ROUTE } from '../../../../../../shared/constants/auth';
import {
  ACTIVATION_CHECKLIST_ITEM,
  CLICK_ACTION,
  COMPLETE_ACTIVATION,
  SCOPE_PANEL
} from '../../../../../../shared/constants/tagManager';
import { useBroker } from '../../../../../../shared/hooks/useBroker/useBroker';
import { useProspects } from '../../../../../../shared/hooks/useProspect/useProspect';
import { useQuoteActions } from '../../../../../../shared/hooks/useQuoteActions/useQuoteActions';
import type { IDataCheck } from '../../../../../../shared/interfaces/checks';
import type { IProspect } from '../../../../../../shared/interfaces/prospects';
import type { IQuote } from '../../../../../../shared/interfaces/quotes';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_PROSPECTS } from '../../../../../../shared/translations/prospects';
import {
  buildActivationCheckGroups,
  buildProspectActivationGroup,
  getActivationQuotes,
  isReadyToActivate
} from '../../../../../../shared/utils/checks/activationChecks';
import ProspectActionsMenu from '../../components/ProspectActionsMenu/ProspectActionsMenu';
import QuotationCard from '../../components/QuotationCard/QuotationCard';
import QuoteFeedback from '../../components/QuoteFeedback/QuoteFeedback';
import './PendingActivationSection.scss';

interface IPendingActivationSectionProps {
  prospect: IProspect;
  quotes: IQuote[];
  onConfirmActivation: (id: number) => void;
  onCompleteFields: (id: number) => void;
  onMarkAsLost: (id: number) => void;
  onMarkAsInactive: (id: number) => void;
  isUpdatingStatus?: boolean;
}

const PendingActivationSection = ({
  prospect,
  quotes,
  onConfirmActivation,
  onCompleteFields,
  onMarkAsLost,
  onMarkAsInactive,
  isUpdatingStatus = false
}: IPendingActivationSectionProps) => {
  const { t } = useTranslation();
  const [otherCollapsed, setOtherCollapsed] = useState(true);
  const [activationCollapsed, setActivationCollapsed] = useState(true);

  const navigate = useNavigate();
  const { selectedActivationGroup } = useProspects();
  const { addonTypeById } = useBroker();

  const {
    localQuotes,
    toast,
    setToast,
    unacceptTarget,
    downloadingQuoteId,
    handleNewQuote,
    handleEditQuote,
    handleDownloadQuote,
    handleAcceptQuote,
    handleConfirmUnaccept,
    handleCancelUnaccept
  } = useQuoteActions(quotes);

  const accepted = getActivationQuotes(localQuotes);
  const others = localQuotes.filter((q) => q.status !== 'accepted');

  const prospectGroup = selectedActivationGroup ?? buildProspectActivationGroup(prospect);
  const checkGroups = buildActivationCheckGroups(prospect, localQuotes, addonTypeById, prospectGroup);
  const allComplete = isReadyToActivate(prospect, localQuotes, addonTypeById, prospectGroup);

  const handleCheckClick = (check: IDataCheck) => {
    if (check.analyticsTarget) {
      TagManagerService.pushEvent(CLICK_ACTION, ACTIVATION_CHECKLIST_ITEM, {
        ...SCOPE_PANEL,
        target: check.analyticsTarget
      });
    }
    navigate(`${PROSPECTS_ROUTE}/${prospect.id}`, {
      state: { activationCheck: { quoteId: check.quoteId } }
    });
  };

  const handleClickButton = () => {
    if (allComplete) {
      onConfirmActivation(prospect.id);
    } else {
      TagManagerService.pushEvent(CLICK_ACTION, COMPLETE_ACTIVATION);
      onCompleteFields(prospect.id);
    }
  };

  return (
    <div className="pending-activation-section">
      {allComplete ? (
        <div className="pending-activation-section__ready">
          <div className="pending-activation-section__ready-title">
            <div className="pending-activation-section__ready-icon">
              <SuccessFill />
            </div>
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.readyTitle)}
          </div>
          <div className="pending-activation-section__ready-body">
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.readyBody)}
          </div>
        </div>
      ) : (
        <ActivationChecklist groups={checkGroups} onCheckClick={handleCheckClick} />
      )}

      <div className="pending-activation-section__accepted">
        <button
          type="button"
          className="pending-activation-section__accepted-header"
          onClick={() => setActivationCollapsed((v) => !v)}
        >
          <span className="pending-activation-section__label">
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.acceptedQuotes)}
          </span>
          <ChevronIcon
            className={`pending-activation-section__chevron${activationCollapsed ? '' : ' pending-activation-section__chevron--open'}`}
          />
        </button>

        {!activationCollapsed && (
          <div className="pending-activation-section__cards">
            {accepted.map((q) => (
              <QuotationCard
                key={q.id}
                quote={q}
                onAccept={(qid) => handleAcceptQuote(prospect.id, qid)}
                onEdit={(qid) => handleEditQuote(prospect.id, qid)}
                onDownload={(qid) => handleDownloadQuote(prospect.id, qid)}
                isInteractive={false}
                isDownloading={downloadingQuoteId === q.id}
              />
            ))}
          </div>
        )}
      </div>

      <div className="pending-activation-section__other">
        <button
          type="button"
          className="pending-activation-section__other-header"
          onClick={() => setOtherCollapsed((v) => !v)}
        >
          <span className="pending-activation-section__label">
            {t(TRANSLATIONS_PROSPECTS.detail.pendingActivation.otherQuotes)}
          </span>
          <ChevronIcon
            className={`pending-activation-section__chevron${otherCollapsed ? '' : ' pending-activation-section__chevron--open'}`}
          />
        </button>

        {!otherCollapsed && (
          <div className="pending-activation-section__cards">
            {others.map((q) => (
              <QuotationCard
                key={q.id}
                quote={q}
                onAccept={(qid) => handleAcceptQuote(prospect.id, qid)}
                onEdit={(qid) => handleEditQuote(prospect.id, qid)}
                onDownload={(qid) => handleDownloadQuote(prospect.id, qid)}
                isDownloading={downloadingQuoteId === q.id}
              />
            ))}
            <CustomButton
              variant="default"
              size="s"
              className="pending-activation-section__new-quote"
              onClick={() => handleNewQuote(prospect.id)}
            >
              <PlusIcon className="pending-activation-section__plus" />
              {t(TRANSLATIONS_PROSPECTS.detail.quoted.newQuote)}
            </CustomButton>
          </div>
        )}
      </div>

      <div className="pending-activation-section__footer">
        <div className="pending-activation-section__cta">
          <CustomButton variant="primary" onClick={() => handleClickButton()}>
            {t(
              allComplete
                ? TRANSLATIONS_PROSPECTS.detail.pendingActivation.activate
                : TRANSLATIONS_PROSPECTS.detail.pendingActivation.completeActivation
            )}
            <RightArrowIcon className="pending-activation-section__arrow" />
          </CustomButton>
        </div>

        <ProspectActionsMenu
          prospectId={prospect.id}
          onMarkAsLost={onMarkAsLost}
          onMarkAsInactive={onMarkAsInactive}
          disabled={isUpdatingStatus}
        />
      </div>

      <QuoteFeedback
        prospect={prospect}
        toast={toast}
        setToast={setToast}
        unacceptTarget={unacceptTarget}
        onCancelUnaccept={handleCancelUnaccept}
        onConfirmUnaccept={handleConfirmUnaccept}
      />
    </div>
  );
};

export default PendingActivationSection;
